import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { createTheme, ThemeProvider, useMediaQuery } from "@mui/material";
import { Snackbar, Alert } from "@mui/material";

const ColorModeContext = createContext();
export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }) => {
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState(
    () => localStorage.getItem("theme") || "system"
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const effectiveMode =
    mode === "system" ? (systemPrefersDark ? "dark" : "light") : mode;

  const updateTheme = (newMode, notify = true) => {
    setMode(newMode);
    localStorage.setItem("theme", newMode);
    if (notify) {
      setSnackbarMessage(
        `Switched to ${newMode.charAt(0).toUpperCase() + newMode.slice(1)} Mode`
      );
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => setSnackbarOpen(false);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    if (!storedTheme) {
      localStorage.setItem("theme", "system");
    }

    const handleStorageChange = (e) => {
      if (e.key === "theme" && e.newValue !== mode) {
        setMode(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [mode]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: effectiveMode,
          ...(effectiveMode === "dark"
            ? {
                background: {
                  default: "#121212",
                  paper: "#1E1E1E",
                },
              }
            : {}),
        },
      }),
    [effectiveMode]
  );

  return (
    <ColorModeContext.Provider
      value={{
        mode,
        setTheme: updateTheme,
        toggleColorMode: () =>
          updateTheme(effectiveMode === "dark" ? "light" : "dark"),
      }}
    >
      <ThemeProvider theme={theme}>
        {children}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleClose} severity="success" variant="filled">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
